<script lang="ts">
	import type { Snippet } from 'svelte';
	import type { Cost, Distance, Duration, RouteSelectorType } from '../types';
	import Badge from '$lib/components/atoms/badge/Badge.svelte';
	import Button from '$lib/components/atoms/button/Button.svelte';
	import Icon from '$lib/components/icons/Icon.svelte';
	import * as c from '$lib/i18n/computedMessages';
	import * as m from '$paraglide/messages';
	import ListGroup from './ListGroup.svelte';

	interface Props {
		routes?: RouteSelectorType[];
		selectedRouteIndex?: number;
		summary: Snippet;
		durationColumn: Snippet<[Duration, boolean, boolean]>;
		distanceColumn: Snippet<[Distance]>;
		emissionsColumn: Snippet<[number]>;
		consumptionColumn: Snippet<[number]>;
		costColumn: Snippet<[Cost[]]>;
		collapsed?: boolean;
		errorType?: 'noRoad' | 'noAddress' | undefined;
		onClickOnSummary?: () => void;
		onClickOnSummaryCollapse?: () => void;
		onIndexChange?: (index: number) => void;
	}

	const {
		routes,
		selectedRouteIndex,
		summary,
		durationColumn,
		distanceColumn,
		emissionsColumn,
		costColumn,
		consumptionColumn,
		errorType,
		collapsed = false,
		onClickOnSummary,
		onClickOnSummaryCollapse,
		onIndexChange,
	}: Props = $props();

	function handleRouteClick(route: RouteSelectorType, e: MouseEvent | KeyboardEvent) {
		e.stopPropagation();

		if (collapsed) {
			return;
		}

		onIndexChange?.(route.index);
	}
</script>

<ListGroup
	list={routes}
	error={errorType}
	{...(collapsed ? { onGlobalClick: onClickOnSummaryCollapse } : { onClick: onClickOnSummary })}
>
	{#snippet header()}
		<!-- waitpoints and badges area -->
		{@render summary?.()}

		{#if errorType || !collapsed}
			<!-- edit route button -->
			<Button
				icon="edit"
				variant="ghost"
				class="grid-area-[button] bg-neutral-weakest hover:bg-neutral-weak text-sm top-0 right-2 shadow-smooth-bottom-2 rd-full w-9 h-9 p-0"
				ariaLabel={m.itinerary__edit()}
				dataTestId="edit-route"
			/>
		{:else}
			<!-- collapse route button -->
			<Button
				icon="chevron-right"
				variant="ghost"
				class="text-sm grid-area-[button] top-0 right-2 rd-full w-9 h-9 p-0  shadow-smooth-bottom-2 bg-primary-weakest"
				ariaLabel={m.itinerary__edit()}
				iconClasses="rotate-90"
				dataTestId="expand-route"
			/>
		{/if}
	{/snippet}
	{#snippet listItem(route)}
		{@const isSelected = route.index === selectedRouteIndex}
		<div
			class={[
				'route-group-row text-coloredneutral grid grid-cols-4 p-2',
				isSelected && !collapsed && 'highlight-route-group rd-3',
				collapsed && !isSelected && 'hidden',
				collapsed ? 'cursor-default' : 'cursor-pointer',
			]}
			role="button"
			tabindex="0"
			data-testid={`route-group-row-${route.index}`}
			onclick={e => handleRouteClick(route, e)}
			onkeydown={(e: KeyboardEvent) => e.key === 'Enter' && handleRouteClick(route, e)}
		>
			<div data-testid="duration" class={[isSelected && !collapsed && 'text-coloredneutral-weakest']} role="cell" aria-label={m.route__route_selector__column_duration()}>
				{@render durationColumn(route.duration, route.isDiscovery && !collapsed, isSelected)}
			</div>
			<div data-testid="cost" class={[isSelected && !collapsed && 'text-coloredneutral-weakest']} role="cell" aria-label={m.route__route_selector__column_cost()}>
				{@render costColumn(route?.cost)}
			</div>
			<div data-testid="distance" class={[isSelected && !collapsed && 'text-coloredneutral-weakest']} role="cell" aria-label={m.route__route_selector__column_distance()}>
				{@render distanceColumn(route.distance)}
			</div>
			{#if route.isEv}
				<div data-testid="consumption" class={[isSelected && !collapsed && 'text-coloredneutral-weakest']} role="cell" aria-label={m.route__route_selector__column_electricity_consumption()}>
					{@render consumptionColumn(route.consumption)}
				</div>
			{:else}
				<div data-testid="emissions" class={[isSelected && !collapsed && 'text-coloredneutral-weakest']} role="cell" aria-label={m.route__route_selector__column_emission()}>
					{@render emissionsColumn(route.emissions)}
				</div>
			{/if}
		</div>
		{#if (route.isDiscovery || route.trafficImpactDuration) && collapsed && isSelected}
			<!-- badge expanded -->
			<div class="pl-2 pb-2 cursor-pointer flex gap-2">
				{#if route.trafficImpactDuration}
					{@const hours = route.trafficImpactDuration?.hours ? `${route.trafficImpactDuration.hours} h` : ''}
					{@const minutes = route.trafficImpactDuration?.minutes ? `${route.trafficImpactDuration.minutes} min` : ''}
					<Badge
						icon="traffic-jam"
						isSquare
						size="sm"
						text={`${hours} ${minutes}`}
						class="font-size-3.5 font-400 px-1.5 py-1.75 bg-neutral-weak w-5 h-6"
						colorClasses="text-traffic-saturated!"
						labelClasses="pl-1"
					/>
				{/if}
				{#if route.isDiscovery}
					<Badge
						icon="scenic-road"
						isSquare
						size="sm"
						text={c.routeBadge('discovery')}
						class="font-size-3.5 font-400 px-0 bg-discovery-weak w-5 h-6"
						colorClasses="text-discovery!"
						labelClasses="pl-0.5"
					/>
				{/if}
			</div>
		{/if}
	{/snippet}
	{#snippet errorItem()}
		<div class="flex items-center gap-x-4 pl-2">
			<Icon icon={errorType === 'noAddress' ? 'no-address' : 'no-road'} class=" w-11.25 h-11.25" />
			<p class="text-coloredneutral-soft font-400 font-size-3.5 line-height-4 m-0">
				{errorType === 'noAddress' ? m.route__route_selector_no_address() : m.route__route_selector_no_road()}
				<br />{m.route__route_selector_error_message()}
			</p>
		</div>
	{/snippet}
</ListGroup>
<style>
	.route-group-row {
		grid-template-columns: 1.4fr 1fr 1fr 1fr;

		@media screen and (max-width: 640px) {
			& {
				--uno: grid-cols-3;
				grid-template-columns: 1.4fr 1fr 1fr;

				& div:last-child {
					--uno: hidden;
				}
			}

		}
	}

	.highlight-route-group {
		background: linear-gradient(to right, rgba(29, 49, 64, 1), rgba(29, 49, 64, 0.9));
	}
</style>
